/**
 * EncounterSearchBar
 *
 * Debounced search input for filtering encounters by patient name,
 * type, location, chief complaint, and provider.
 * Enterprise design with larger input and refined focus states.
 */

import React, { useEffect } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useEncounterList } from '../EncounterListContext';
import { useDebounceSearch } from '../hooks/useDebounceSearch';

export function EncounterSearchBar() {
  const { config, filters, setFilters } = useEncounterList();

  const { searchTerm, setSearchTerm, debouncedTerm, isSearching, clearSearch } =
    useDebounceSearch({
      delay: 300,
      minLength: 2,
    });

  // Sync debounced term with filters
  useEffect(() => {
    if (debouncedTerm !== filters.search) {
      setFilters({ search: debouncedTerm || undefined });
    }
  }, [debouncedTerm, filters.search, setFilters]);

  // Initialize search term from filters
  useEffect(() => {
    if (filters.search && !searchTerm) {
      setSearchTerm(filters.search);
    }
  }, []);

  if (config.enableSearch === false) {
    return null;
  }

  const placeholder =
    config.mode === 'connected'
      ? 'Search by patient, MRN, type, location...'
      : 'Search by patient, type, location, provider...';

  return (
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
        {isSearching ? (
          <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
        ) : (
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        )}
      </div>
      <input
        type="text"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder={placeholder}
        className="block w-full pl-12 pr-10 py-3 text-sm bg-gray-50/50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all placeholder:text-gray-400"
        aria-label="Search encounters"
      />
      {searchTerm && (
        <button
          type="button"
          onClick={clearSearch}
          className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Clear search"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}

export default EncounterSearchBar;
